import * as React from 'react';
import { toast } from 'sonner';
import { Copy, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';

import { useTenant } from '@/hooks';
import { useDeleteMilestoneMutation, useDeleteExternalMilestoneMutation } from '@/state';

export function MilestoneActions({
  milestoneId,
  milestoneTitle,
  onEdit,
  onDuplicate,
  isExternal,
}: {
  milestoneId: number;
  milestoneTitle: string;
  onEdit: () => void;
  onDuplicate: () => void;
  isExternal?: boolean;
}) {
  const { tenant } = useTenant();
  const showDuplicate = false;
  const [openConfirm, setOpenConfirm] = React.useState(false);
  const [deleteMilestone, { isLoading: isDeletingInternal }] = useDeleteMilestoneMutation();
  const [deleteExternalMilestone, { isLoading: isDeletingExternal }] = useDeleteExternalMilestoneMutation();

  const isLoading = isDeletingInternal || isDeletingExternal;

  const onDelete = React.useCallback(async () => {
    if (!isExternal && !tenant) return toast.message('Tenant missing');

    try {
      if (isExternal) {
        await deleteExternalMilestone({ id: milestoneId }).unwrap();
      } else {
        await deleteMilestone({ tenant: tenant!, id: milestoneId }).unwrap();
      }
      toast.success('Milestone deleted');
      setOpenConfirm(false);
    } catch (e: any) {
      toast.error(e?.data?.message || 'Failed to delete milestone');
    }
  }, [tenant, milestoneId, deleteMilestone, deleteExternalMilestone, isExternal]);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            className={cn(
              'h-8 w-8 rounded-2xl',
              'hover:bg-muted/60',
              'focus-visible:ring-2 focus-visible:ring-primary/30'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="rounded-2xl">
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            <Pencil className="mr-2 h-4 w-4" /> Edit
          </DropdownMenuItem>
          {!isExternal && showDuplicate && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate();
              }}
            >
              <Copy className="mr-2 h-4 w-4" /> Duplicate
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            className="text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              setOpenConfirm(true);
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={openConfirm} onOpenChange={setOpenConfirm}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete milestone?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{' '}
              <span className="font-medium">{milestoneTitle}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} disabled={isLoading}>
              {isLoading ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
